import { useRef, useState } from "react";
import { API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { downloadFile } from "@/lib/download";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, Download, Upload, DatabaseZap, Trash2, Loader2 } from "lucide-react";
import ClearDataDialog from "./ClearDataDialog";
import ResetAudit from "./ResetAudit";

function SeedDemoDialog({ open, busy, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy && !v) onCancel(); }}>
      <DialogContent data-testid="seed-demo-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-blue-700"><DatabaseZap className="h-5 w-5" /> Load Demo Data</DialogTitle>
          <DialogDescription>Repopulates sample orders, BOMs, suppliers, machines and OSP vendors so the app is demo-ready. Existing records are kept; missing demo items are restored.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onCancel} data-testid="seed-demo-cancel">Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={onConfirm} data-testid="seed-demo-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DatabaseZap className="h-4 w-4 mr-1.5" />} Load Demo Data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Export / restore-from-file / demo seed / clear-data, plus the reset audit trail.
export default function DangerZone({ onClearData, onSeedDemo, onImported }) {
  const [auditKey, setAuditKey] = useState(0);
  const bump = () => setAuditKey((k) => k + 1);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const fileRef = useRef(null);

  const exportBackup = async () => {
    setExporting(true);
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    try { await downloadFile(`${API}/admin/export-backup`, `proplan-backup-${stamp}.xlsx`); toast.success("Backup downloaded"); }
    catch (e) { logger.error("Export backup failed:", e); toast.error("Could not export backup"); }
    finally { setExporting(false); }
  };
  const restoreFromFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const csrf = (document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/) || [])[1] || "";
      const res = await fetch(`${API}/admin/restore-backup`, { method: "POST", credentials: "include", headers: { "X-CSRF-Token": decodeURIComponent(csrf) }, body: fd });
      const data = await res.json();
      if (!res.ok) throw data;
      toast.success(data.message || "Backup restored");
      onImported?.();
      bump();
    } catch (err) {
      logger.error("Restore from file failed:", err);
      toast.error((err && err.detail) || "Could not restore backup");
    } finally { setRestoring(false); }
  };
  const runSeed = async () => {
    setSeeding(true);
    try { await onSeedDemo(); setSeedOpen(false); bump(); }
    catch (e) { logger.error("Seed demo failed:", e); }
    finally { setSeeding(false); }
  };
  const runClear = async (sections) => { await onClearData(sections); bump(); };

  return (
    <div className="xl:col-span-3 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="danger-zone">
      <div className="lg:col-span-2 bg-white rounded-xl border border-red-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <h3 className="font-display text-base font-semibold text-red-700">Danger Zone &amp; Data Tools</h3>
        </div>
        <p className="text-xs text-slate-500 max-w-2xl">
          Export a full backup, restore from one, load a fresh demo dataset, or wipe data by section.
          <span className="font-semibold text-slate-700"> User accounts are always preserved.</span>
        </p>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={restoreFromFile} data-testid="restore-backup-input" />
        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" className="border-slate-300" disabled={exporting} onClick={exportBackup} data-testid="export-backup-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export Backup
          </Button>
          <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800" disabled={restoring} onClick={() => fileRef.current?.click()} data-testid="restore-backup-btn">
            {restoring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Restore Backup
          </Button>
          <Button variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700" onClick={() => setSeedOpen(true)} data-testid="load-demo-btn">
            <DatabaseZap className="h-4 w-4 mr-1.5" /> Load Demo Data
          </Button>
          <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setClearOpen(true)} data-testid="open-clear-data-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Clear Data
          </Button>
        </div>
      </div>
      <ResetAudit refreshKey={auditKey} />
      <SeedDemoDialog open={seedOpen} busy={seeding} onCancel={() => setSeedOpen(false)} onConfirm={runSeed} />
      <ClearDataDialog open={clearOpen} onClose={() => setClearOpen(false)} onClear={runClear} />
    </div>
  );
}
